import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ElementType,
  GalleryType,
  SortDirection,
  SortType,
  PublicPrivateType,
  IGameTemplate,
  IUserProfile,
  GradeTarget,
} from '@righton/networking';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { APIClientsContext } from '../lib/context/APIClientsContext';
import { useTSAPIClientsContext } from '../hooks/context/useAPIClientsContext';
import {
  useCentralDataState,
  useCentralDataDispatch,
} from '../hooks/context/useCentralDataContext';
import { ScreenSize, GameQuestionType } from '../lib/CentralModels';
import {
  ExploreGamesMainContainer,
  ExploreGamesUpperContainer,
} from '../lib/styledcomponents/ExploreGamesStyledComponents';
import Recommended from '../components/explore/Recommended';
import { FEATURED_GAME_COUNT } from '../lib/FeaturedGamesModels';
import GamesLibraryGallery from '../components/cardgallery/GamesLibraryGallery';
import ExploreLauncherBar from '../components/explorelauncher/ExploreLauncherBar';
import mathSymbolsBackground from '../images/mathSymbolsBackground.svg';
import CentralButton from '../components/button/Button';
import { ButtonType } from '../components/button/ButtonModels';

interface ExploreGamesProps {
  screenSize: ScreenSize;
  setIsTabsOpen: (isTabsOpen: boolean) => void;
  fetchElements: () => void;
  handleChooseGrades: (grades: GradeTarget[]) => void;
  handleSortChange: (newSort: {
    field: SortType;
    direction: SortDirection | null;
  }) => void;
  handleSearchChange: (searchString: string) => void;
}

export default function ExploreGames({
  screenSize,
  setIsTabsOpen,
  fetchElements,
  handleChooseGrades,
  handleSortChange,
  handleSearchChange,
}: ExploreGamesProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const apiClients = useTSAPIClientsContext(APIClientsContext);
  const centralData = useCentralDataState();
  const centralDataDispatch = useCentralDataDispatch();

  const [gameSet, setGameSet] = useState<IGameTemplate[]>([]);
  const [imgSrc, setImgSrc] = useState<string>();
  /**
   * Searching now happens on Browse: ExploreLauncherBar navigates there rather
   * than filtering in place, so this screen has no search-results state of its
   * own. Left as a constant because central searchTerms can still be set by
   * Browse, and browser-Back would otherwise show an empty searched-* gallery
   * with Recommended hidden.
   */
  const isSearchResults = false;
  // mirrors the isLibraryInit lifecycle in LibraryTabs: the flag defaults true so
  // this fires on mount, and dispatching isLoading here (rather than mid-render)
  // means the gallery's skeletons gate on a committed value
  useEffect(() => {
    if (centralData.isExploreInit) {
      centralDataDispatch({ type: 'SET_IS_EXPLORE_INIT', payload: false });
      const needsFetch =
        centralData.recommendedGames.length === 0 ||
        centralData.mostPopularGames.length === 0;
      if (needsFetch) {
        centralDataDispatch({ type: 'SET_IS_LOADING', payload: true });
        fetchElements();
      }
    }
  }, [centralData.isExploreInit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (game: IGameTemplate, games: IGameTemplate[]) => {
    centralDataDispatch({ type: 'SET_SELECTED_GAME', payload: null });
    setGameSet(games);
    navigate(`/games/${game.publicPrivateType}/${game.id}`);
  };

  return (
    <ExploreGamesMainContainer id="scrollableDiv">
      <ExploreGamesUpperContainer screenSize={screenSize}>
        {!isSearchResults && (
          <img
            src={mathSymbolsBackground}
            alt="Math Symbol Background"
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              bottom: '0',
              zIndex: 0,
              objectFit: 'none',
              overflow: 'hidden',
            }}
          />
        )}
        <ExploreLauncherBar
          screenSize={screenSize}
          browseTarget={GameQuestionType.GAME}
        />
        {!isSearchResults && (
          <Recommended<IGameTemplate>
            screenSize={screenSize}
            recommendedElements={centralData.recommendedGames}
            elementType={ElementType.GAME}
            setIsTabsOpen={setIsTabsOpen}
            handleView={handleView}
            slideCount={FEATURED_GAME_COUNT}
          />
        )}
      </ExploreGamesUpperContainer>
      <Box
        style={{
          width: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          backgroundColor: theme.palette.primary.creamBackgroundColor,
        }}
      >
        <GamesLibraryGallery
          screenSize={screenSize}
          galleryElements={
            isSearchResults
              ? centralData.searchedGames
              : centralData.mostPopularGames
          }
          handleView={handleView}
          isLoading={centralData.isLoading}
          footer={
            <CentralButton
              buttonType={ButtonType.BROWSEALLGAMES}
              isEnabled
              smallScreenOverride
              // matches the game card's button: card width less its 24px side padding
              buttonWidthOverride={
                screenSize === ScreenSize.LARGE ? '336px' : '279px'
              }
              onClick={() => navigate('/browse/game')}
            />
          }
        />
      </Box>
      <Box
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexGrow: 1,
          backgroundColor: theme.palette.primary.creamBackgroundColor,
        }}
      />
    </ExploreGamesMainContainer>
  );
}
